import { supabase } from './supabaseClient';

export const kpiAdminService = {
  // KPI Systems
  async getKPISystems() {
    const { data, error } = await supabase
      .from('kpi_systems')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async createKPISystem(systemData) {
    const { data, error } = await supabase
      .from('kpi_systems')
      .insert([systemData])
      .select();
    if (error) throw error;
    return data[0];
  },

  async updateKPISystem(systemId, updates) {
    const { data, error } = await supabase
      .from('kpi_systems')
      .update(updates)
      .eq('id', systemId)
      .select();
    if (error) throw error;
    return data[0];
  },

  async deactivateKPISystem(systemId) {
    return this.updateKPISystem(systemId, { is_active: false });
  },

  // KPI Definitions
  async getKPIDefinitions(systemId) {
    const { data, error } = await supabase
      .from('kpi_definitions')
      .select('*')
      .eq('kpi_system_id', systemId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async createKPIDefinition(definitionData) {
    const { data, error } = await supabase
      .from('kpi_definitions')
      .insert([definitionData])
      .select();
    if (error) throw error;
    return data[0];
  },

  async updateKPIDefinition(definitionId, updates) {
    const { data, error } = await supabase
      .from('kpi_definitions')
      .update(updates)
      .eq('id', definitionId)
      .select();
    if (error) throw error;
    return data[0];
  },

  async deleteKPIDefinition(definitionId) {
    const { error } = await supabase
      .from('kpi_definitions')
      .delete()
      .eq('id', definitionId);
    if (error) throw error;
  },

  // KPI Scoring Rules
  async getScoringRules(definitionId) {
    const { data, error } = await supabase
      .from('kpi_scoring_rules')
      .select('*')
      .eq('kpi_definition_id', definitionId)
      .order('period', { ascending: false });
    if (error) throw error;
    return data;
  },

  async createScoringRule(ruleData) {
    const { data, error } = await supabase
      .from('kpi_scoring_rules')
      .insert([ruleData])
      .select();
    if (error) throw error;
    return data[0];
  },

  async updateScoringRule(ruleId, updates) {
    const { data, error } = await supabase
      .from('kpi_scoring_rules')
      .update(updates)
      .eq('id', ruleId)
      .select();
    if (error) throw error;
    return data[0];
  },

  async deleteScoringRule(ruleId) {
    const { error } = await supabase
      .from('kpi_scoring_rules')
      .delete()
      .eq('id', ruleId);
    if (error) throw error;
  },

  // User KPI System Assignments
  async getAllUsers() {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, name, position, role_id, roles ( name )')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data || []).map((u) => ({ ...u, role: u.roles?.name ?? null }));
  },

  async getUserAssignments() {
    const { data, error } = await supabase
      .from('user_kpi_system_assignments')
      .select('*, user_id, kpi_system_id')
      .order('assigned_date', { ascending: false });
    if (error) throw error;
    return data;
  },

  async assignKPISystemToUser(userId, systemId) {
    const { data, error } = await supabase
      .from('user_kpi_system_assignments')
      .insert([{
        user_id: userId,
        kpi_system_id: systemId,
        is_active: true,
      }])
      .select();
    if (error) throw error;
    return data[0];
  },

  async removeKPISystemFromUser(userId, systemId) {
    const { error } = await supabase
      .from('user_kpi_system_assignments')
      .delete()
      .eq('user_id', userId)
      .eq('kpi_system_id', systemId);
    if (error) throw error;
  },

  async deactivateUserAssignment(assignmentId) {
    const { data, error } = await supabase
      .from('user_kpi_system_assignments')
      .update({ is_active: false })
      .eq('id', assignmentId)
      .select();
    if (error) throw error;
    return data[0];
  },

  // Reference queries for read-only library
  async getAllDefinitionsWithRules() {
    const { data, error } = await supabase
      .from('kpi_definitions')
      .select('*, kpi_systems(name, country), kpi_scoring_rules(*)')
      .order('kpi_system_id', { ascending: true });
    if (error) throw error;
    return data;
  },
};
